import { useEffect, useMemo, useRef } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import { EXPERIMENT_ASSET_LABELS, NOTIFICATION_CONFIG, NOTIFICATION_MESSAGES } from '@/shared/lib/constants';
import type { AssetTreeNode } from '@/shared/lib/tree';
import { findNodeByKey, updateNodeChildren } from '@/shared/lib/tree';

import { useTreeExpandStore } from '@/entities/discovery';

import { searchIntegrationWithPagination } from '@/features/dataSearch/api/integrationSearchApi';

export const RUN_ASSETS_QUERY_KEY = 'run-assets';

export const useRunExpand = () => {
  const queryClient = useQueryClient();
  const loadingKeys = useTreeExpandStore((state) => state.loadingKeys);
  const treeData = useTreeExpandStore((state) => state.treeData);
  const processedRunRef = useRef<string | null>(null);

  const expandingRunKey = useMemo(
    () =>
      loadingKeys.find(
        (key) =>
          String(key).startsWith('run-') &&
          !String(key).includes('-feature_set') &&
          !String(key).includes('-dataset') &&
          !String(key).includes('-parameter_set') &&
          !String(key).includes('-artifact')
      ),
    [loadingKeys]
  );

  const expandingRun = useMemo(() => {
    if (!expandingRunKey) return null;
    return findNodeByKey(treeData, String(expandingRunKey));
  }, [treeData, expandingRunKey]);

  const runData = expandingRun?.data as { runId?: string; runName?: string } | undefined;
  const runId = runData?.runId;

  const {
    data: assetsData,
    isSuccess,
    isError,
  } = useQuery({
    queryKey: [RUN_ASSETS_QUERY_KEY, runId],
    queryFn: async () => {
      if (!runId) return { featureSet: [], dataset: [], artifact: [] };

      const [featureSetRes, datasetRes, artifactRes] = await Promise.all([
        searchIntegrationWithPagination({
          runId: Number(runId),
          assetTypes: ['run_feature_set'],
          size: 100,
        }),
        searchIntegrationWithPagination({
          runId: Number(runId),
          assetTypes: ['run_dataset'],
          size: 100,
        }),
        searchIntegrationWithPagination({
          runId: Number(runId),
          assetTypes: ['run_artifact'],
          size: 100,
        }),
      ]);

      return {
        featureSet: featureSetRes.results,
        dataset: datasetRes.results,
        artifact: artifactRes.results,
      };
    },
    enabled: !!runId && !!expandingRun,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  useEffect(() => {
    if (isSuccess && assetsData && expandingRun && runId) {
      if (processedRunRef.current === runId) {
        return;
      }

      const childAssets: AssetTreeNode[] = [];

      if (assetsData.featureSet.length > 0) {
        childAssets.push({
          key: `run-${runId}-feature_set`,
          title: EXPERIMENT_ASSET_LABELS.feature_set,
          type: 'feature_set',
          isLeaf: true,
          data: {
            runId,
            runName: runData?.runName,
            entityType: 'feature_set',
          },
        });
      }

      if (assetsData.dataset.length > 0) {
        childAssets.push({
          key: `run-${runId}-dataset`,
          title: EXPERIMENT_ASSET_LABELS.dataset,
          type: 'dataset',
          isLeaf: true,
          data: {
            runId,
            runName: runData?.runName,
            entityType: 'dataset',
          },
        });
      }

      if (assetsData.artifact.length > 0) {
        childAssets.push({
          key: `run-${runId}-artifact`,
          title: EXPERIMENT_ASSET_LABELS.artifact,
          type: 'artifact',
          isLeaf: true,
          data: {
            runId,
            runName: runData?.runName,
            entityType: 'artifact',
          },
        });
      }

      const currentTreeData = useTreeExpandStore.getState().treeData;
      const updatedTreeData = updateNodeChildren(currentTreeData, expandingRun.key, childAssets);

      useTreeExpandStore.getState().setTreeData(updatedTreeData);

      useTreeExpandStore.getState().addLoadedNode(expandingRun.key);
      useTreeExpandStore.getState().addExpandedKey(expandingRun.key);
      useTreeExpandStore.getState().removeLoadingKey(expandingRun.key);

      processedRunRef.current = runId;
    }
  }, [isSuccess, assetsData, expandingRun, runId, runData?.runName]);

  useEffect(() => {
    if (isError && expandingRun) {
      useTreeExpandStore.getState().removeLoadingKey(expandingRun.key);
      useTreeExpandStore.getState().removeExpandedKey(expandingRun.key);

      queryClient.removeQueries({
        queryKey: [RUN_ASSETS_QUERY_KEY, runId],
      });

      notification.error({
        message: NOTIFICATION_MESSAGES.RUN_SUB_ASSETS_FETCH_ERROR,
        description: NOTIFICATION_MESSAGES.RUN_SUB_ASSETS_FETCH_ERROR_DESC,
        placement: NOTIFICATION_CONFIG.PLACEMENT,
        duration: NOTIFICATION_CONFIG.DURATION.ERROR,
      });

      processedRunRef.current = null;
    }
  }, [isError, expandingRun, runId, queryClient]);
};
